import { useState } from "react";
import { DayPicker, type DateRange as DayPickerRange } from "react-day-picker";
import "react-day-picker/style.css";
import { CalendarRange } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui-ext/primitives";
import { formatDateEuropean } from "@/lib/engineering-insights/chart-data";
import { dateToIsoLocal, isoToLocalDate } from "@/lib/engineering-insights/date-range";
import { cn } from "@/lib/utils";

export function DateRangePopover({
  from,
  to,
  isCustom,
  onApply,
}: {
  from: string;
  to: string;
  isCustom: boolean;
  onApply: (range: { from: string; to: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DayPickerRange | undefined>();

  const openWithDraft = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft({ from: isoToLocalDate(from), to: isoToLocalDate(to) });
    }
    setOpen(nextOpen);
  };

  // Once a complete range is drafted, the next click starts a fresh range —
  // otherwise DayPicker just nudges the nearest edge of the seeded range.
  const handleSelect = (next: DayPickerRange | undefined, triggerDate: Date) => {
    if (draft?.from && draft.to) {
      setDraft({ from: triggerDate, to: undefined });
      return;
    }
    setDraft(next);
  };

  const apply = () => {
    if (!draft?.from || !draft.to) return;
    onApply({ from: dateToIsoLocal(draft.from), to: dateToIsoLocal(draft.to) });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={openWithDraft}>
      <PopoverTrigger asChild>
        <button
          aria-label="Custom date range"
          className={cn(
            "inline-flex h-8 items-center gap-1.5 px-3 rounded-[10px] text-xs font-medium transition-colors ring-focus",
            isCustom
              ? "bg-[var(--primary)] text-[color:var(--primary-foreground)]"
              : "text-anchor hover:bg-muted border border-soft",
          )}
        >
          <CalendarRange className="h-3.5 w-3.5" />
          {formatDateEuropean(from)} → {formatDateEuropean(to)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="ei-calendar w-auto p-3 bg-surface border-soft rounded-[14px] shadow-card">
        <DayPicker
          mode="range"
          numberOfMonths={2}
          selected={draft}
          onSelect={handleSelect}
          defaultMonth={draft?.from}
          disabled={{ after: new Date() }}
        />
        <div className="mt-2 flex items-center justify-between gap-3 border-t border-soft pt-3">
          <span className="text-xs text-muted-foreground">
            {draft?.from && draft.to
              ? `${formatDateEuropean(dateToIsoLocal(draft.from))} → ${formatDateEuropean(dateToIsoLocal(draft.to))}`
              : "Pick a start and end date"}
          </span>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={!draft?.from || !draft.to} onClick={apply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
